import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocationSegments } from "../../../../../hooks/use-location-segments";
import { passwordConditions } from "../../helpers/password-validation-helper";
import { setUser } from "../../../../../reducers/slices/user";
export function useRegisterState() {
    const { segments } = useLocationSegments();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.userState);
    // Seed defaults from redux on first render so that if the form ever remounts
    // (e.g. after a failed sign-up) it rehydrates with whatever the user has
    // already typed instead of dropping back to empty strings.
    const initialDefaults = useRef({
        show_customer_code: "false",
        customer_code: "",
        name: user.user.name ?? "",
        surname: user.user.surname ?? "",
        email: user.user.email ?? "",
        cell_number: user.user.cell_number ?? "",
        delivery_method: "email",
        password: user.user.password ?? "",
        confirm_password: user.user.confirm_password ?? "",
        get_communication: user.user.get_communication ?? false,
        agrees_terms_and_conditions: user.user.agrees_terms_and_conditions ?? false,
    });
    const { control, formState: { errors, isValid, isReady }, handleSubmit: submitForm, register, setValue, watch, } = useForm({
        mode: "all",
        defaultValues: initialDefaults.current,
    });
    const password = watch("password");
    // Note: cell_number validation is handled via Controller rules in register.tsx
    register("password", {
        required: { value: true, message: "Password is required" },
        deps: ["confirm_password"],
        validate: {
            isValidPassword: (v) => {
                const { strength, length, specialChars } = passwordConditions(v);
                if (length < 8)
                    return "Password must be at least 8 characters long";
                if (!specialChars)
                    return "Password must contain at least one special character";
                return true;
            },
        },
    });
    register("confirm_password", {
        required: { value: true, message: "Please confirm your password" },
        validate: {
            isValidPassword: (v) => {
                if (v !== password)
                    return "Passwords do not match";
                return true;
            },
        },
    });
    const agrees = watch("agrees_terms_and_conditions");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { strength: passwordStrength, length: passwordLength, specialChars: passwordSpecialChars } = passwordConditions(password);
    const showCustomerCode = watch("show_customer_code");
    useEffect(() => {
        if (showCustomerCode !== "true") {
            setValue("customer_code", "");
        }
    }, [showCustomerCode, setValue]);
    // Mirror form values into redux as the user types so that nothing typed
    // can be lost — even if RHF state is wiped by a remount or some sibling
    // rerender, the next render reseeds defaults from redux.
    const userRef = useRef(user);
    useEffect(() => {
        userRef.current = user;
    }, [user]);
    useEffect(() => {
        const subscription = watch((values) => {
            const latest = userRef.current;
            dispatch(setUser({
                ...latest,
                user: {
                    ...latest.user,
                    name: values.name ?? latest.user.name,
                    surname: values.surname ?? latest.user.surname,
                    email: values.email ?? latest.user.email,
                    cell_number: values.cell_number ?? latest.user.cell_number,
                    password: values.password ?? latest.user.password,
                    confirm_password: values.confirm_password ?? latest.user.confirm_password,
                    get_communication: values.get_communication ?? latest.user.get_communication,
                    agrees_terms_and_conditions: values.agrees_terms_and_conditions ?? latest.user.agrees_terms_and_conditions,
                },
            }));
        });
        return () => subscription.unsubscribe();
    }, [watch, dispatch]);
    const canSubmit = useMemo(() => !!isValid && agrees, [isValid, agrees]);
    const lastSegment = useMemo(() => (segments ? segments?.[segments?.length - 1] : "register"), [segments]);
    const title = useMemo(() => (lastSegment === "register" ? "Sign Up" : "Sign In"), [lastSegment]);
    const handleTogglePassword = useCallback(() => {
        setShowPassword((prevValue) => !prevValue);
    }, []);
    const handleToggleConfirmPassword = useCallback(() => {
        setShowConfirmPassword((prevValue) => !prevValue);
    }, []);
    return useMemo(() => ({
        canSubmit,
        control,
        errors,
        isReady,
        handleToggleConfirmPassword,
        handleTogglePassword,
        passwordLength,
        passwordSpecialChars,
        passwordStrength,
        register,
        setValue,
        showConfirmPassword,
        showCustomerCode,
        showPassword,
        submitForm,
        title,
    }), [
        canSubmit,
        control,
        errors,
        isReady,
        handleToggleConfirmPassword,
        handleTogglePassword,
        passwordLength,
        passwordSpecialChars,
        passwordStrength,
        register,
        setValue,
        showConfirmPassword,
        showCustomerCode,
        showPassword,
        submitForm,
        title,
    ]);
}
